import { computed, ref, unref, type MaybeRef } from 'vue';
import { areEquals, dot } from '@kitware/vtk.js/Common/Core/Math';
import type { Vector3 } from '@kitware/vtk.js/types';
import type { useCurrentImage } from '@/src/composables/useCurrentImage';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import {
  extentContains,
  extentSize,
  fullExtent,
  isEmptyExtent,
} from '@/src/segmentation/geometry';
import type { OccupiedDepthRequest } from '@/src/segmentation/measurements/useOccupiedDepth';
import type { SlabSpecification } from '@/src/core/slab/types';
import { getOccupiedDepthSupport } from './occupiedDepthSupport';
import { useEnFaceViewStore } from './store';

export type OccupiedDepthProjection = {
  source: Pick<
    ReturnType<typeof useCurrentImage>,
    'currentImageID' | 'currentImageData'
  >;
  slab: MaybeRef<SlabSpecification>;
  /** Blocks every thickness control, such as an unavailable projection. */
  blockedReason: MaybeRef<string | null>;
};

/** Binds the selected segment mask and calibrated slab to a measurement request. */
export function useOccupiedDepthInput(
  viewId: MaybeRef<string>,
  { source, slab, blockedReason }: OccupiedDepthProjection
) {
  const { currentImageID, currentImageData } = source;
  const segmentationStore = useSegmentationStore();
  const segmentsRegistry = useSegmentStore().segments;
  const enFaceStore = useEnFaceViewStore();
  const settings = computed(() =>
    enFaceStore.settingsFor(unref(viewId), currentImageID.value)
  );
  // Separate fields keep projection method changes from invalidating requests.
  const slabAxis = computed(() => unref(slab).axis);
  const slabFirst = computed(() => unref(slab).first);
  const slabLast = computed(() => unref(slab).last);
  const volumeRevision = ref(0);
  const maskRevision = ref(0);
  const support = computed(() => {
    void volumeRevision.value;
    return getOccupiedDepthSupport(currentImageID.value);
  });

  const storedSegment = computed(() =>
    segmentsRegistry.getSegment(settings.value.segmentId)
  );
  const segmentOptions = computed(() => {
    if (!currentImageID.value) return [];
    const segmentIds = segmentationStore
      .imageMasks(currentImageID.value)
      .map((mask) => mask.segmentId);
    const selected = storedSegment.value;
    // A saved segment stays listed without a mask, as while restored voxels decode.
    if (selected && !segmentIds.includes(selected.id))
      segmentIds.push(selected.id);
    return segmentIds.map((segmentId) => {
      const appearance = segmentsRegistry.appearanceOf(segmentId);
      return {
        value: segmentId,
        title: appearance.displayName,
        color: appearance.cssColor,
      };
    });
  });
  // Saved choices stay raw; a deleted one shows the first option, unhighlighted.
  const segmentId = computed(
    () => storedSegment.value?.id ?? segmentOptions.value[0]?.value ?? null
  );
  const highlightThin = computed(
    () => settings.value.highlightThin && !!storedSegment.value
  );
  const selectedMask = computed(() =>
    segmentationStore.maskFor(currentImageID.value, segmentId.value)
  );
  const selectedBinding = computed(() =>
    selectedMask.value
      ? segmentationStore.findMaskBinding(selectedMask.value.id)
      : undefined
  );
  const segmentationReason = computed(
    () =>
      unref(blockedReason) ||
      (!segmentOptions.value.length
        ? 'Load or create a segmentation mask associated with this volume.'
        : null)
  );
  const measurementMask = computed(() => {
    void maskRevision.value;
    const binding = selectedBinding.value;
    if (!binding) return null;
    const scalars = binding.image.getPointData().getScalars();
    const values = scalars?.getData();
    if (
      !(values instanceof Uint8Array) ||
      scalars.getNumberOfComponents() !== 1
    )
      return null;
    return {
      values,
      dimensions: binding.image.getDimensions(),
      extent: binding.extent,
    };
  });
  const maskStorageReason = computed(() => {
    const binding = selectedBinding.value;
    if (!binding)
      return 'The selected segment voxels are not available; load or create its mask to measure occupied depth.';
    if (isEmptyExtent(binding.extent))
      return 'The selected segment has no occupied voxels to measure.';
    if (!measurementMask.value)
      return 'The selected segment does not have a supported binary voxel mask.';
    return null;
  });
  const maskGeometryReason = computed(() => {
    void volumeRevision.value;
    void maskRevision.value;
    const binding = selectedBinding.value;
    const parent = currentImageData.value;
    if (!binding || !parent) return null;
    const extent = binding.extent;
    const validExtent = extentContains(
      fullExtent(parent.getDimensions()),
      extent
    );
    const direction = parent.getDirection();
    const bases = [0, 1, 2].map((axis) => {
      const offset = axis * 3;
      const basis: Vector3 = [
        direction[offset],
        direction[offset + 1],
        direction[offset + 2],
      ];
      const length = Math.hypot(...basis);
      return Number.isFinite(length) && length > 0
        ? (basis.map((value) => value / length) as Vector3)
        : null;
    });
    const orthogonal = [
      [0, 1],
      [0, 2],
      [1, 2],
    ].every(([a, b]) => {
      const left = bases[a];
      const right = bases[b];
      return (
        left !== null && right !== null && Math.abs(dot(left, right)) < 1e-4
      );
    });
    if (!orthogonal)
      return 'Occupied depth requires an orthogonal source voxel grid.';
    const gridMatches =
      areEquals(binding.image.getDimensions(), extentSize(extent), 1e-5) &&
      areEquals(binding.image.getSpacing(), parent.getSpacing(), 1e-5) &&
      areEquals(
        [...binding.image.getDirection()],
        [...parent.getDirection()],
        1e-5
      );
    const originMatches = areEquals(
      binding.image.getOrigin(),
      [...parent.indexToWorld([extent[0], extent[2], extent[4]])],
      1e-5
    );
    return validExtent && gridMatches && originMatches
      ? null
      : 'This segmentation mask does not match the source voxel grid; align it to the volume before measuring thickness.';
  });
  const micronsPerVoxel = computed(() => {
    void volumeRevision.value;
    const image = currentImageData.value;
    if (!image) return 0;
    const axis = slabAxis.value;
    // Declared DICOM spacing is physical, so rounded unit cosines must not
    // rescale it. Generic image directions can carry scale.
    const scale = support.value.calibratedAxes?.[axis]
      ? 1
      : Math.hypot(...image.getDirection().slice(axis * 3, axis * 3 + 3));
    return Math.abs(image.getSpacing()[axis]) * scale * 1000;
  });
  const highlightReason = computed(
    () =>
      segmentationReason.value ||
      (!selectedBinding.value ? maskStorageReason.value : null) ||
      support.value.reason ||
      (support.value.calibratedAxes?.[slabAxis.value] === false
        ? 'The projection axis has no reliable declared voxel spacing; occupied depth is unavailable.'
        : null) ||
      (!Number.isFinite(micronsPerVoxel.value) || micronsPerVoxel.value <= 0
        ? 'The projection axis has invalid voxel spacing; occupied depth is unavailable.'
        : null)
  );
  const thicknessReason = computed(
    () =>
      highlightReason.value ||
      maskStorageReason.value ||
      maskGeometryReason.value
  );

  // Scalar and mask storage can change in place during streaming or segmentation editing.
  const volumeScalars = computed(() => {
    void volumeRevision.value;
    return currentImageData.value?.getPointData().getScalars();
  });
  const maskImage = computed(() => selectedBinding.value?.image);
  const maskScalars = computed(() => {
    void maskRevision.value;
    return maskImage.value?.getPointData().getScalars();
  });
  onVTKEvent(currentImageData, 'onModified', () => {
    volumeRevision.value += 1;
  });
  onVTKEvent(volumeScalars, 'onModified', () => {
    volumeRevision.value += 1;
  });
  onVTKEvent(maskImage, 'onModified', () => {
    maskRevision.value += 1;
  });
  onVTKEvent(maskScalars, 'onModified', () => {
    maskRevision.value += 1;
  });

  const request = computed((): OccupiedDepthRequest | null => {
    if (!highlightThin.value) return null;
    void volumeRevision.value;
    const image = currentImageData.value;
    const mask = measurementMask.value;
    if (!image || !mask || thicknessReason.value) return null;
    return {
      dimensions: image.getDimensions(),
      source: image,
      segment: selectedMask.value!.id,
      grid: JSON.stringify([
        image.getOrigin(),
        image.getSpacing(),
        image.getDirection(),
      ]),
      axis: slabAxis.value,
      first: slabFirst.value,
      last: slabLast.value,
      mask,
    };
  });

  return {
    settings,
    segmentId,
    highlightThin,
    selectedMask,
    segmentOptions,
    segmentationReason,
    highlightReason,
    thicknessReason,
    micronsPerVoxel,
    request,
  };
}
